import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { DataTable } from "components/molecules/table/table";
import { DataTableSkeleton, PageHeader, SectionCard, StatCard, StatusPill } from "components/admin";
import {
  getWalletRecipients,
  validateWalletTopUpDraft,
  validateWalletTransferDraft,
} from "pages/app/workspace/sections/helpers/account-section.helpers";
import {
  WalletTopUpForm,
  WalletTransferForm,
} from "pages/app/workspace/sections/forms/wallet-section-forms";
import {
  SectionContentGrid,
  SectionStatsGrid,
} from "pages/app/workspace/sections/styles/workspace-section.styles";
import { workspaceApi } from "pages/app/workspace/services";
import { formatDateTime, formatMoney, getApiErrorMessage } from "utils/admin";
import { hasPortalCapability } from "utils/portal-access";
import {
  WalletSectionSection,
  WalletSectionGridLayout,
} from "pages/app/workspace/sections/styles/wallet-section.inline.styles";
export const WalletSection: React.FC<{
  currentUser: any;
}> = ({ currentUser }) => {
  const queryClient = useQueryClient();
  const [topUpAmount, setTopUpAmount] = useState("");
  const [transferAmount, setTransferAmount] = useState("");
  const [recipientUserId, setRecipientUserId] = useState("");
  const [description, setDescription] = useState("");
  const { data: wallet, isLoading } = useQuery({
    queryKey: ["workspace-wallet"],
    queryFn: workspaceApi.wallets.me,
  });
  const { data: walletTransactions } = useQuery({
    queryKey: ["workspace-wallet-transactions"],
    queryFn: () =>
      workspaceApi.walletTransactions.list({
        page: 1,
        per_page: 50,
      }),
  });
  const { data: users = [] } = useQuery({
    queryKey: ["workspace-wallet-users"],
    queryFn: () => workspaceApi.users.list(),
    enabled: hasPortalCapability(currentUser?.portal_access, "can_manage_users"),
  });
  const topUpMutation = useMutation({
    mutationFn: () =>
      workspaceApi.wallets.topUp(wallet.id, {
        amount: Number(topUpAmount),
        description: description || "Manual top-up",
      }),
    onSuccess: () => {
      toast.success("Wallet topped up.");
      setTopUpAmount("");
      setDescription("");
      queryClient.invalidateQueries({
        queryKey: ["workspace-wallet"],
      });
      queryClient.invalidateQueries({
        queryKey: ["workspace-wallet-transactions"],
      });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, "Unable to top up this wallet."));
    },
  });
  const transferMutation = useMutation({
    mutationFn: () =>
      workspaceApi.wallets.transfer(wallet.id, {
        amount: Number(transferAmount),
        recipient_user_id: recipientUserId,
        description: description || "Wallet transfer",
      }),
    onSuccess: () => {
      toast.success("Transfer completed.");
      setTransferAmount("");
      setRecipientUserId("");
      setDescription("");
      queryClient.invalidateQueries({
        queryKey: ["workspace-wallet"],
      });
      queryClient.invalidateQueries({
        queryKey: ["workspace-wallet-transactions"],
      });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, "Unable to complete this transfer."));
    },
  });
  const transactions = walletTransactions?.transactions || [];
  const recipients = getWalletRecipients(users, currentUser?.id);
  const topUpErrors = validateWalletTopUpDraft(topUpAmount);
  const transferErrors = validateWalletTransferDraft({
    recipientUserId,
    transferAmount,
  });
  const handleTopUpSubmit = () => {
    if (topUpErrors.amount) {
      toast.error(topUpErrors.amount);
      return;
    }
    topUpMutation.mutate();
  };
  const handleTransferSubmit = () => {
    const firstError = transferErrors.recipientUserId || transferErrors.transferAmount;
    if (firstError) {
      toast.error(firstError);
      return;
    }
    transferMutation.mutate();
  };
  return (
    <>
      <PageHeader
        title="Wallet"
        description="Review wallet balance, browse transactions, top up funds, and transfer value to another account."
      />

      <SectionStatsGrid>
        <StatCard
          label="Balance"
          value={wallet?.formatted_balance || formatMoney(wallet?.balance)}
        />
        <StatCard label="Currency" value={wallet?.currency || "ZAR"} />
        <StatCard label="Transactions" value={wallet?.transaction_count ?? transactions.length} />
        <StatCard
          label="Recent Activity"
          value={transactions.length ? formatDateTime(transactions[0].created_at) : "No activity"}
        />
      </SectionStatsGrid>

      <WalletSectionSection>
        <SectionContentGrid>
          <SectionCard subtitle="Wallet actions" title="Move Funds">
            <WalletSectionGridLayout>
              <WalletTopUpForm
                amount={topUpAmount}
                amountError={topUpErrors.amount}
                isSubmitting={topUpMutation.isPending}
                onAmountChange={setTopUpAmount}
                onSubmit={handleTopUpSubmit}
                submitDisabled={!wallet?.id || !topUpAmount || topUpMutation.isPending}
              />
              <WalletTransferForm
                description={description}
                isSubmitting={transferMutation.isPending}
                onDescriptionChange={setDescription}
                onRecipientChange={setRecipientUserId}
                onSubmit={handleTransferSubmit}
                onTransferAmountChange={setTransferAmount}
                recipientError={transferErrors.recipientUserId}
                recipientUserId={recipientUserId}
                recipients={recipients}
                submitDisabled={
                  !wallet?.id || !recipientUserId || !transferAmount || transferMutation.isPending
                }
                transferAmountError={transferErrors.transferAmount}
                transferAmount={transferAmount}
              />
            </WalletSectionGridLayout>
          </SectionCard>

          <SectionCard subtitle="Recent wallet transactions" title="Transactions">
            <DataTable
              canEdit={false}
              canRemove={false}
              columns={[
                {
                  label: "Description",
                  accessor: (row: any) => row.description || "No description",
                },
                {
                  label: "Type",
                  accessor: (row: any) => <StatusPill value={row.transaction_type} />,
                },
                {
                  label: "Amount",
                  accessor: (row: any) => row.formatted_amount || formatMoney(row.amount),
                },
                {
                  label: "Created",
                  accessor: (row: any) => formatDateTime(row.created_at),
                },
              ]}
              data={transactions}
              emptyDescription="Wallet transactions will appear here after top-ups, transfers, or purchases."
              emptyTitle="No wallet transactions"
              hideNav
              isLoading={isLoading}
              loadingState={<DataTableSkeleton columns={4} rows={5} />}
            />
          </SectionCard>
        </SectionContentGrid>
      </WalletSectionSection>
    </>
  );
};
